import { redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { toResult } from "#/modules/util.auth/api-errors";
import { apiFailure } from "#/modules/util.errors/api-failure.server";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import {
  createLes,
  getBeweegactiviteitenreeksen,
  getBeweegthemasFilteredByBeweegactiviteiten,
  getLesgroepen,
  getSociaalEmotioneleThemas,
  updateLes,
  updateLesPrecheck,
} from "#/modules/les.start-flow/api.start-lesson.server";
import { getLesEditData } from "#/modules/les.docent-gestuurd/api.les.docent.server";
import { FALLBACK_AANTAL_GROEPEN, type StartLessonFilters } from "#/modules/les.start-flow/filters";
import { getClassesForStartLesson } from "#/modules/school.klassen/api.klassen.server";
import { getLessonsOverviewSearchParamsForScheduledCalendarDay } from "#/modules/school.lessen/params.lessen-overview";
import { client } from "#/modules/util.fetch-client/fetch-client.server";
import type { components } from "#/schema";

export const getLesFlowLayoutData = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(
    (data: {
      beweegactiviteitenreeksSearchTerm: string | null;
      sociaalEmotioneelThemasReeksSearchTerm: string | null;
      includeDefaultsBeweeg: boolean;
      includeDefaultsSociaal: boolean;
    }) => data,
  )
  .handler(async ({ data }) => {
    const [
      klassenResult,
      beweegactiviteitenreeksenResult,
      socialeThemasResult,
      allBeweegactiviteitenResult,
      allSociaalEmotioneelThemasResult,
    ] = await Promise.all([
      client.GET("/school/klassen", { params: {} }).then(toResult),
      getBeweegactiviteitenreeksen(
        data.beweegactiviteitenreeksSearchTerm || "",
        data.includeDefaultsBeweeg,
      ),
      getSociaalEmotioneleThemas(
        data.sociaalEmotioneelThemasReeksSearchTerm || "",
        data.includeDefaultsSociaal,
      ),
      getBeweegactiviteitenreeksen("", data.includeDefaultsBeweeg),
      getSociaalEmotioneleThemas("", data.includeDefaultsSociaal),
    ]);

    if (Result.isError(beweegactiviteitenreeksenResult))
      throw beweegactiviteitenreeksenResult.error;
    if (Result.isError(klassenResult)) throw klassenResult.error;
    if (Result.isError(socialeThemasResult)) throw socialeThemasResult.error;
    if (Result.isError(allBeweegactiviteitenResult)) throw allBeweegactiviteitenResult.error;
    if (Result.isError(allSociaalEmotioneelThemasResult))
      throw allSociaalEmotioneelThemasResult.error;

    return {
      klassen: klassenResult.value!.data,
      beweegactiviteitenreeksen: beweegactiviteitenreeksenResult.value,
      socialeThemas: socialeThemasResult.value || { domeinen: [] },
      allBeweegactiviteiten: allBeweegactiviteitenResult.value,
      allSociaalEmotioneelThemas: allSociaalEmotioneelThemasResult.value,
    };
  });

export const getLesFlowStep1Data = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async () => {
    const result = await getClassesForStartLesson();
    if (Result.isError(result)) throw result.error;
    return { klassen: result.value.data };
  });

/**
 * Edit mode only: goals removed from a les that already has results need explicit confirmation.
 * Returns the pending deletions; the client shows a dialog before continuing to step 3.
 */
export const precheckLesFlowGoals = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator((data: { lesId: string; filters: StartLessonFilters }) => data)
  .handler(async ({ data }) => {
    const { filters } = data;
    const result = await updateLesPrecheck({
      lesId: data.lesId,
      beweegactiviteiten: filters.beweegactiviteiten,
      sociaalEmotioneelThemas: filters.sociaalEmotioneelThemas,
      filter: {
        // Step 3's beforeLoad backfilled this; the fallback only narrows the type.
        aantalGroepen: filters.aantalGroepen ?? FALLBACK_AANTAL_GROEPEN,
        beweegthema: filters.beweegthema ?? undefined,
        groepeerType: filters.groepeerType,
        seed: String(filters.seed),
        lockedLeerlingen: filters.lockedLeerlingen,
        excludedLeerlingen: filters.excludedLeerlingen,
      },
    });
    if (Result.isError(result)) return apiFailure(result.error);
    return result.value;
  });

// POST: a long lockedLeerlingen list in the GET query string exceeds the header limit (431).
export const getLesFlowStep3Data = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(
    (
      data: Pick<
        StartLessonFilters,
        | "klasIds"
        | "aantalGroepen"
        | "groepeerType"
        | "beweegthema"
        | "seed"
        | "lockedLeerlingen"
        | "excludedLeerlingen"
        | "beweegactiviteiten"
        | "editLesId"
      >,
    ) => data,
  )
  .handler(async ({ data }) => {
    const filteredBeweegthemasResult = await getBeweegthemasFilteredByBeweegactiviteiten(
      data.beweegactiviteiten,
    );
    if (Result.isError(filteredBeweegthemasResult)) throw filteredBeweegthemasResult.error;

    const lesGroepenResult = await getLesgroepen({
      klasIds: data.klasIds,
      filter: {
        aantalGroepen: data.aantalGroepen ?? FALLBACK_AANTAL_GROEPEN,
        groepeerType: data.groepeerType,
        beweegthema: data.beweegthema ?? undefined,
        seed: String(data.seed),
        lockedLeerlingen: data.lockedLeerlingen,
        excludedLeerlingen: data.excludedLeerlingen,
      },
    });
    if (Result.isError(lesGroepenResult)) throw lesGroepenResult.error;

    // Edit mode loads the existing les (for `canEditDate` gating of the schedule button).
    let getDocentGestuurdeLesData: components["schemas"]["GetLesEditDataResponse"] | null = null;
    if (data.editLesId) {
      const lesEditDataResult = await getLesEditData(data.editLesId);
      if (Result.isError(lesEditDataResult)) throw lesEditDataResult.error;
      getDocentGestuurdeLesData = lesEditDataResult.value;
    }

    return {
      lesGroepen: lesGroepenResult.value,
      filteredBeweegthemas: filteredBeweegthemasResult.value,
      getDocentGestuurdeLesData,
    };
  });

export const submitLesFlowStep3 = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator(
    (data: {
      locale: string;
      schoolyear?: string;
      school: string;
      intent: string;
      filters: StartLessonFilters;
    }) => data,
  )
  .handler(async ({ data }) => {
    const { filters } = data;
    const { klasIds, groepeerType, beweegthema } = filters;
    // Step 3's beforeLoad backfilled this; the fallback only narrows the type.
    const aantalGroepen = filters.aantalGroepen ?? FALLBACK_AANTAL_GROEPEN;

    // Edit mode: update the existing les instead of creating a new one, then return to it.
    if (filters.editLesId) {
      const updateResult = await updateLes({
        lesId: filters.editLesId,
        beweegactiviteiten: filters.beweegactiviteiten,
        sociaalEmotioneelThemas: filters.sociaalEmotioneelThemas,
        date: filters.scheduledDate || new Date().toISOString(),
        filter: {
          aantalGroepen,
          beweegthema: beweegthema ?? undefined,
          groepeerType,
          seed: String(filters.seed),
          lockedLeerlingen: filters.lockedLeerlingen,
          excludedLeerlingen: filters.excludedLeerlingen,
        },
      });
      if (Result.isError(updateResult)) return apiFailure(updateResult.error);

      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/scores",
        params: {
          locale: data.locale,
          schoolyear: data.schoolyear,
          school: data.school,
          lesson: filters.editLesId,
        },
      });
    }

    const createResult = await createLes({
      klasIds,
      beweegactiviteiten: filters.beweegactiviteiten,
      sociaalEmotioneelThemas: filters.sociaalEmotioneelThemas,
      isConcept: data.intent === "save",
      startedAt: data.intent === "save" ? (filters.scheduledDate ?? undefined) : undefined,
      filter: {
        aantalGroepen,
        beweegthema: beweegthema ?? undefined,
        groepeerType,
        seed: String(filters.seed),
        lockedLeerlingen: filters.lockedLeerlingen,
        excludedLeerlingen: filters.excludedLeerlingen,
      },
    });
    if (Result.isError(createResult)) return apiFailure(createResult.error);

    if (data.intent === "save") {
      // The lessons route validates + strips default search params itself (validateSearch +
      // stripSearchParams), so the tab filter and one-time highlight travel as a plain object.
      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/lessons",
        params: { locale: data.locale, schoolyear: data.schoolyear, school: data.school },
        search: {
          ...getLessonsOverviewSearchParamsForScheduledCalendarDay(filters.scheduledDate),
          highlightLesson: createResult.value.id,
        },
      });
    }

    throw redirect({
      to: "/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/scores",
      params: {
        locale: data.locale,
        schoolyear: data.schoolyear,
        school: data.school,
        lesson: createResult.value.id,
      },
    });
  });
